import React, { useState } from 'react';
import {
	Box,
	Button,
	Card,
	CardActionArea,
	Chip,
	Container,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import StorageIcon from '@mui/icons-material/Storage';
import GroupsIcon from '@mui/icons-material/Groups';
import ApiIcon from '@mui/icons-material/Api';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import SyncProblemIcon from '@mui/icons-material/SyncProblem';
import ReactGA from 'react-ga4';
import { useAppDispatch, useAppSelector } from '../hooks';
import useAuth from '../components/Auth';
import { entriesSetState, selectAllRepositories } from './contentSlice';
import { useResetWorkspaceMutation } from '../services/companies';
import { useApiKeysFindQuery } from '../services/apiKeys';
import { useLoadRepositoriesQuery } from '../services/repositoryEntries';
import { localStorageSetIsIntroDialogCompleted, obtainLocalEntries } from '../localStorage';
import { useCookieConsent } from '../contexts/CookieConsentContext';
import SyncOfflineDataDialog from '../components/SyncOfflineDataDialog';
import { Entry } from '../types';

const RESET_WORKSPACE_CONFIRM_PHRASE = 'delete everything';

function SettingsTile({ to, icon, title, description, isDisabled = false }: { to: string, icon: React.ReactNode, title: string, description: string, isDisabled?: boolean }) {
	return (
		<Card
			sx={{
				position: 'relative',
				backgroundColor: 'transparent',
				boxShadow: '0 2px 4px rgba(0,0,0,0.1), 0 1px 2px rgba(0,0,0,0.1)',
			}}
		>
			<CardActionArea
				component={Link}
				to={to}
				disabled={isDisabled}
				sx={{ height: '100%', p: 3, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'flex-start', gap: 1.5 }}
			>
				<Box sx={{ color: isDisabled ? 'text.disabled' : 'primary.main', '& svg': { fontSize: 40 } }}>{icon}</Box>
				<Typography variant='h6' component='h2' color={isDisabled ? 'text.disabled' : 'text.primary'}>{title}</Typography>
				<Typography variant='body2' color={isDisabled ? 'text.disabled' : 'text.secondary'}>{description}</Typography>
			</CardActionArea>
			{isDisabled && (
				<Tooltip title='Not available in offline mode. Sign in to use it.' disableInteractive>
					<Chip
						icon={<CloudOffIcon />}
						label='Offline'
						size='small'
						variant='outlined'
						color='warning'
						sx={{ position: 'absolute', top: 12, right: 12, userSelect: 'none' }}
					/>
				</Tooltip>
			)}
		</Card>
	);
}

function formatCollectionCount(count: number) {
	if (count === 0) {
		return 'No collections';
	}
	return count === 1 ? '1 collection' : `${count} collections`;
}

function formatActiveKeyCount(count: number) {
	if (count === 0) {
		return 'No active keys';
	}
	return count === 1 ? '1 active key' : `${count} active keys`;
}

function SettingsView() {
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const { isAuthenticated, userProfile } = useAuth();
	const { data: apiKeys } = useApiKeysFindQuery(undefined, { skip: !isAuthenticated });
	const { data: remoteRepositories } = useLoadRepositoriesQuery(undefined, { skip: !isAuthenticated });
	const localRepositories = useAppSelector(selectAllRepositories);
	const repositories = isAuthenticated ? remoteRepositories : localRepositories;
	let apiKeysDescription = 'Access your workspace from another machine or an LLM';
	if (isAuthenticated) {
		apiKeysDescription = apiKeys ? formatActiveKeyCount(apiKeys.length) : '';
	}
	const { analyticsConsent } = useCookieConsent();
	const [resetWorkspace, { isLoading: isResettingWorkspace }] = useResetWorkspaceMutation();
	const [isResetWorkspaceDialogOpen, setIsResetWorkspaceDialogOpen] = useState(false);
	const [resetWorkspaceConfirmText, setResetWorkspaceConfirmText] = useState('');
	const [resetWorkspaceError, setResetWorkspaceError] = useState<string | null>(null);
	const [localRecordsToSync, setLocalRecordsToSync] = useState<Entry[]>(() => (isAuthenticated ? obtainLocalEntries() : []));
	const [isSyncDialogOpen, setIsSyncDialogOpen] = useState(false);
	const isResetWorkspaceConfirmValid =
		!isAuthenticated || resetWorkspaceConfirmText.trim() === RESET_WORKSPACE_CONFIRM_PHRASE;

	const onResetWorkspaceClick = () => {
		setResetWorkspaceError(null);
		setResetWorkspaceConfirmText('');
		setIsResetWorkspaceDialogOpen(true);
	};

	const onResetWorkspaceDialogClose = () => {
		if (!isResettingWorkspace) {
			setIsResetWorkspaceDialogOpen(false);
			setResetWorkspaceError(null);
			setResetWorkspaceConfirmText('');
		}
	};

	const clearOfflineWorkspace = () => {
		localStorageSetIsIntroDialogCompleted(true);
		dispatch(entriesSetState([]));
		setIsResetWorkspaceDialogOpen(false);
		navigate('/');
		if (analyticsConsent) {
			ReactGA.event({
				category: 'UI',
				action: 'IntroClearLocalWorkspaceClicked',
			});
		}
	};

	const onResetWorkspaceConfirm = async () => {
		setResetWorkspaceError(null);
		if (!isAuthenticated) {
			clearOfflineWorkspace();
			return;
		}
		try {
			await resetWorkspace().unwrap();
			setIsResetWorkspaceDialogOpen(false);
			navigate('/');
			ReactGA.event({
				category: 'Workspace',
				action: 'ResetWorkspace',
			});
		} catch {
			setResetWorkspaceError('Could not reset workspace. Please try again.');
		}
	};

	return (
		<Box minHeight='calc(100dvh - 72px)' display='flex' flexDirection='column'>
			<Box flex={1}>
				<Container maxWidth='md' sx={{ mb: 5, display: 'flex', flexDirection: 'column', gap: 3 }}>
					<Typography variant='h4' component='h1' sx={{ mt: 2 }}>
						Settings
					</Typography>
					<Box
						display='grid'
						gridTemplateColumns={{ xs: '1fr', sm: 'repeat(3, 1fr)' }}
						gap={2}
					>
						<SettingsTile
							to='/settings/collections'
							icon={<StorageIcon />}
							title='Collections'
							description={repositories ? formatCollectionCount(repositories.length) : ''}
						/>
						<SettingsTile
							to='/settings/team'
							icon={<GroupsIcon />}
							title='Team'
							description={isAuthenticated ? userProfile.companyName : 'Share a workspace with your team'}
							isDisabled={!isAuthenticated}
						/>
						<SettingsTile
							to='/settings/api'
							icon={<ApiIcon />}
							title='API keys'
							description={apiKeysDescription}
							isDisabled={!isAuthenticated}
						/>
					</Box>
					{isAuthenticated && localRecordsToSync.length > 0 && (
						<Box>
							<Typography variant='subtitle2' color='text.secondary' gutterBottom>
								Offline work
							</Typography>
							<Typography variant='body2' color='text.secondary' sx={{ mb: 1.5 }}>
								This device contains work created in offline mode that is not yet in your cloud workspace.
							</Typography>
							<Button
								color='warning'
								variant='outlined'
								startIcon={<SyncProblemIcon />}
								onClick={() => setIsSyncDialogOpen(true)}
							>
								Sync offline data
							</Button>
						</Box>
					)}
				</Container>
			</Box>
			<SyncOfflineDataDialog
				open={isSyncDialogOpen}
				records={localRecordsToSync}
				onClose={() => setIsSyncDialogOpen(false)}
				onNotNow={() => setIsSyncDialogOpen(false)}
				onSynced={() => {
					setLocalRecordsToSync([]);
					setIsSyncDialogOpen(false);
				}}
			/>
			<Box
				component='footer'
				sx={{
					mt: 'auto',
					py: 3,
					borderTop: 1,
					borderColor: 'divider',
				}}
			>
				<Container maxWidth='md'>
					<Typography variant='subtitle2' color='text.secondary' gutterBottom>
						Danger zone
					</Typography>
					<Typography variant='body2' color='text.secondary' sx={{ mb: 1.5 }}>
						{isAuthenticated
							? 'Permanently delete all collections, entries, and workspace data from your account.'
							: 'Remove all collections and entries stored in this browser.'}
					</Typography>
					<Button
						color='error'
						variant='outlined'
						startIcon={<DeleteForeverIcon />}
						onClick={onResetWorkspaceClick}
						disabled={isResettingWorkspace}
					>
						{isAuthenticated ? 'Reset workspace' : 'Clear offline workspace'}
					</Button>
				</Container>
			</Box>
			<Dialog
				open={isResetWorkspaceDialogOpen}
				onClose={onResetWorkspaceDialogClose}
				maxWidth='xs'
				fullWidth
			>
				<DialogTitle>
					{isAuthenticated ? 'Reset workspace?' : 'Clear offline workspace?'}
				</DialogTitle>
				<DialogContent>
					<DialogContentText>
						{isAuthenticated
							? 'This permanently deletes all data from your workspace. This cannot be undone.'
							: 'This removes all collections and entries stored on this device. Your cloud workspace is not affected.'}
					</DialogContentText>
					{isAuthenticated && (
						<TextField
							sx={{ mt: 2 }}
							autoFocus
							margin='dense'
							fullWidth
							label={`Type "${RESET_WORKSPACE_CONFIRM_PHRASE}" to confirm`}
							value={resetWorkspaceConfirmText}
							onChange={(event) => setResetWorkspaceConfirmText(event.target.value)}
							disabled={isResettingWorkspace}
							autoComplete='off'
						/>
					)}
					{resetWorkspaceError && (
						<Typography color='error' variant='body2' sx={{ mt: 2 }}>
							{resetWorkspaceError}
						</Typography>
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={onResetWorkspaceDialogClose} disabled={isResettingWorkspace}>
						Cancel
					</Button>
					<Button
						onClick={onResetWorkspaceConfirm}
						color='error'
						variant='contained'
						disabled={isResettingWorkspace || !isResetWorkspaceConfirmValid}
					>
						{isResettingWorkspace
							? 'Resetting…'
							: isAuthenticated
								? 'Reset workspace'
								: 'Clear offline workspace'}
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
}

export default SettingsView;
